import React, { createContext, ReactNode, useCallback, useState } from "react";
import LoaderTransition from "./LoaderTransition";

export interface LoadingContextType {
  loadingStates: { [key: string]: boolean };
  setLoadingState: (key: string, loading: boolean) => void;
  isAnyLoading: boolean;
  isGlobalLoading: boolean;
  withLoading: (key: string, operation: () => Promise<any>) => Promise<any>;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [loadingStates, setLoadingStates] = useState<{
    [key: string]: boolean;
  }>({});

  const setLoadingState = useCallback((key: string, loading: boolean) => {
    setLoadingStates((prev) => ({
      ...prev,
      [key]: loading,
    }));
  }, []);

  const withLoading = useCallback(
    async (key: string, operation: () => Promise<any>) => {
      setLoadingState(key, true);
      try {
        return await operation();
      } finally {
        setLoadingState(key, false);
      }
    },
    [setLoadingState],
  );
  const isAnyLoading = Object.values(loadingStates).some(Boolean);

  // Lista de chaves que devem ativar o loader global (overlay)
  const globalLoadingKeys = [
    "page-loading",
    "auth-loading",
    "app-loading",
    "route-transition",
  ];

  // Verifica se algum loading global está ativo
  const isGlobalLoading = globalLoadingKeys.some((key) => loadingStates[key]);

  return (
    <LoadingContext.Provider
      value={{
        loadingStates,
        setLoadingState,
        isAnyLoading,
        isGlobalLoading,
        withLoading,
      }}
    >
      {children}
      <LoaderTransition isLoading={isGlobalLoading} />
    </LoadingContext.Provider>
  );
};

export default LoadingContext;
